import React, { useState, useEffect, useRef } from "react";
import { useTheme } from "../../context/ThemeContext";

/**
 * The shape from fetchOptions response:
 *   {
 *     options: Array<{ value: any; label: string }>;
 *     totalPages: number;
 *   }
 *
 * fetchOptions( page: number, search: string ):
 *   Must return that shape. 'totalPages' indicates how many pages exist overall.
 */

interface PaginatedSelectProps {
  fetchOptions: (
    page: number,
    search: string
  ) => Promise<{
    options: any[];
    totalPages: number;
  }>;
  onSelect: (selected: string | string[]) => void;
  placeholder?: string;
  pageSize?: number;
  isMultiple?: boolean;

  // For single-select: if you have a known (value, label) to show initially
  defaultValue?: number | string;
  defaultLabel?: string;
}

const PaginatedSelect: React.FC<PaginatedSelectProps> = ({
  fetchOptions,
  onSelect,
  placeholder = "Search...",
  pageSize = 10,
  isMultiple = false,
  defaultValue,
  defaultLabel,
}) => {
  const [options, setOptions] = useState<any[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);

  // For multiple select: store array of selected items
  const [selectedOptions, setSelectedOptions] = useState<any[]>([]);

  // For single select: store the single selected item
  const [selectedOption, setSelectedOption] = useState<{
    value: any;
    label: string;
  } | null>(null);

  const [isOptionsVisible, setIsOptionsVisible] = useState(false);

  const containerRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const { theme } = useTheme();
  const textColors =
    theme === "night"
      ? "text-gray-500 border-gray-900 border-2 rounded-2xl"
      : "text-primary-700 border-primary-500 border-2 rounded-2xl";

  /**
   * We fetch data whenever:
   *  - The dropdown is open (isOptionsVisible = true)
   *  - currentPage changes
   *  - searchTerm changes
   */
  useEffect(() => {
    if (!isOptionsVisible) return; // If closed, don't fetch

    const fetchDataAsync = async () => {
      setLoading(true);
      try {
        // Call the prop function you pass in
        const { options: newOptions, totalPages: newTotalPages } =
          await fetchOptions(currentPage, searchTerm);

        // If on the first page or user changed search, replace old options.
        // Otherwise, append new items for infinite scrolling.
        setOptions((prev) =>
          currentPage === 1 ? newOptions : [...prev, ...newOptions]
        );
        console.log("newTotalPages =>", currentPage, newTotalPages);

        setTotalPages(newTotalPages);
        setHasMore(currentPage < newTotalPages);
      } catch (error) {
        console.error("Error fetching options:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchDataAsync();
  }, [fetchOptions, currentPage, searchTerm, isOptionsVisible]);

  /**
   * If the user clicks outside the component, close the dropdown
   */
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsOptionsVisible(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  /**
   * If single-select mode, set up the default selection
   */
  useEffect(() => {
    if (
      !isMultiple &&
      defaultValue !== undefined &&
      defaultLabel !== undefined
    ) {
      setSelectedOption({ value: defaultValue, label: defaultLabel });
      setSearchTerm(defaultLabel);
    }
  }, [isMultiple, defaultValue, defaultLabel]);

  /**
   * Infinite scrolling logic:
   * If we scroll close to the bottom, fetch the next page
   */
  const handleScroll = () => {
    if (!dropdownRef.current) return;
    const { scrollTop, scrollHeight, clientHeight } = dropdownRef.current;

    if (scrollHeight - scrollTop <= clientHeight + 50 && hasMore && !loading) {
      setCurrentPage((prev) => prev + 1);
    }
  };

  /**
   * When user types in the search box,
   * reset to page=1, clear old items, and open the dropdown
   */
  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newTerm = e.target.value;
    setSearchTerm(newTerm);
    setCurrentPage(1);
    setOptions([]); // Clear old items so they don't linger
    setIsOptionsVisible(true);
  };

  /**
   * Clicking the arrow toggles the dropdown
   */
  const handleArrowClick = () => {
    setIsOptionsVisible((prev) => !prev);
    if (!isOptionsVisible) {
      setCurrentPage(1);
      setOptions([]);
    }
  };

  /**
   * Focus the input => open dropdown (page=1, new fetch)
   */
  const handleInputFocus = () => {
    setIsOptionsVisible(true);
    setCurrentPage(1);
    setOptions([]);
  };

  /**
   * Toggling item selection for single/multiple
   */
  const handleToggleSelect = (option: any) => {
    if (isMultiple) {
      // For multi-select
      const alreadySelected = selectedOptions.some(
        (sel) => sel.value === option.value
      );
      let updated: any[];
      if (alreadySelected) {
        updated = selectedOptions.filter((sel) => sel.value !== option.value);
      } else {
        updated = [...selectedOptions, option];
      }
      setSelectedOptions(updated);
      // Return array of values to parent
      onSelect(updated.map((x) => x.value));
    } else {
      // For single-select
      setSelectedOption(option);
      onSelect(option.value); // Send the ID or value up
      setSearchTerm(option.label || option.name);
      setIsOptionsVisible(false);
    }
  };

  /**
   * Check if an option is currently selected
   */
  const isSelected = (option: any) => {
    if (isMultiple) {
      return selectedOptions.some((sel) => sel.value === option.value);
    }
    return selectedOption?.value === option.value;
  };

  return (
    <div className="paginated-select relative" ref={containerRef}>
      <div className="relative">
        {/* The main input for searching */}
        <input
          type="text"
          placeholder={placeholder}
          value={
            !isMultiple && selectedOption ? selectedOption.label : searchTerm
          }
          onChange={handleSearch}
          onFocus={handleInputFocus}
          className={`search-input w-full p-2 pr-8 mb-2 ${textColors}`}
        />
        {/* The arrow icon, toggling dropdown */}
        <span
          className="material-icons absolute right-2 top-2 text-gray-600 cursor-pointer"
          onClick={handleArrowClick}
        >
          {isOptionsVisible ? "arrow_drop_up" : "arrow_drop_down"}
        </span>
      </div>

      {/* If multi-select, optionally show selected items as tags */}
      {isMultiple && selectedOptions.length > 0 && (
        <div className="selected-options mb-2">
          {selectedOptions.map((opt) => (
            <span
              key={opt.value}
              className="inline-block bg-accent-100 text-white px-2 py-1 rounded mr-1 mb-1"
            >
              {opt.label || opt.name}
            </span>
          ))}
        </div>
      )}

      {/* The dropdown listing of options */}
      {isOptionsVisible && (
        <div
          ref={dropdownRef}
          className="options-container max-h-60 overflow-y-auto border rounded absolute w-full bg-gray-50 z-50 shadow-xl"
          style={{ zIndex: 9999 }}
          onScroll={handleScroll}
        >
          {options.map((option, index) => {
            const selected = isSelected(option);
            return (
              <div
                key={index}
                onClick={() => handleToggleSelect(option)}
                className={`option-item p-2 flex items-center hover:bg-gray-200 cursor-pointer ${
                  selected ? "bg-secondary-100 font-bold" : ""
                }`}
              >
                {isMultiple && (
                  <input
                    type="checkbox"
                    checked={selected}
                    readOnly
                    className={`mr-2 ${textColors}`}
                  />
                )}
                <span>{option.label || option.name}</span>
              </div>
            );
          })}

          {/* Loading or no-options states */}
          {loading && <div className="p-2">Loading...</div>}
          {!loading && options.length === 0 && (
            <div className="p-2">No options found</div>
          )}
        </div>
      )}
    </div>
  );
};

export default PaginatedSelect;
